import { useCallback, useEffect, useRef, useState } from 'react';
import { ensureFonts, loadImage } from './canvas';
import { withDefaults, type Template, type Values } from './types';

/**
 * Keeps a canvas painted with the current template, values and picture.
 *
 * The canvas is the poster at full size (1080x1080) and is only shown
 * scaled down by CSS, so the preview on screen and the PNG that gets
 * downloaded are the same pixels — there is no separate export path
 * that could drift from what the board sees.
 */
export function usePoster(template: Template, values: Values, pictureSrc: string | null) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [picture, setPicture] = useState<HTMLImageElement | null>(null);
  const [pictureFailed, setPictureFailed] = useState(false);
  const [missingFonts, setMissingFonts] = useState<string[]>([]);
  const [fontsReady, setFontsReady] = useState(false);

  /* Fonts, once. Canvas will happily draw Arial instead and say nothing,
     so nothing is painted until they have actually arrived. */
  useEffect(() => {
    let live = true;
    void ensureFonts(template.fonts).then((missing) => {
      if (!live) return;
      setMissingFonts(missing);
      setFontsReady(true);
    });
    return () => {
      live = false;
    };
  }, [template]);

  /* Picture. */
  useEffect(() => {
    if (!pictureSrc) {
      setPicture(null);
      setPictureFailed(false);
      return;
    }
    let live = true;
    setPictureFailed(false);
    void loadImage(pictureSrc).then((img) => {
      if (!live) return;
      setPicture(img);
      setPictureFailed(img === null);
    });
    return () => {
      live = false;
    };
  }, [pictureSrc]);

  /** Draw right now. */
  const paint = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx || !fontsReady) return;
    ctx.save();
    try {
      template.draw(ctx, withDefaults(template, values), picture);
    } finally {
      ctx.restore();
    }
  }, [template, values, picture, fontsReady]);

  /* Coalesced into a frame so dragging a slider does not queue up a
     redraw per pointer event. */
  useEffect(() => {
    const frame = requestAnimationFrame(paint);
    return () => cancelAnimationFrame(frame);
  }, [paint]);

  /* Both export paths paint first. The effect above only runs on the
     next frame, so reading the canvas straight after an edit — typing
     and hitting save in the same tick — would otherwise capture the
     poster as it was one keystroke ago. */

  /** The poster as a PNG data URL, for saving through the API. */
  const toDataUrl = useCallback((): string => {
    paint();
    return canvasRef.current?.toDataURL('image/png') ?? '';
  }, [paint]);

  /** Hand the PNG to the browser as a download. */
  const download = useCallback((filename: string) => {
    const cv = canvasRef.current;
    if (!cv) return;
    paint();
    cv.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      // Give the click a tick to start before the blob goes away.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, 'image/png');
  }, [paint]);

  return { canvasRef, picture, pictureFailed, missingFonts, fontsReady, toDataUrl, download };
}

/** A filename-safe version of a title: "Reality Fracture" → "reality-fracture". */
export function slug(s: string): string {
  const map: Record<string, string> = {
    á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u', ý: 'y',
    þ: 'th', æ: 'ae', ö: 'o', ð: 'd',
  };
  return (
    s
      .toLowerCase()
      .replace(/[áéíóúýþæöð]/g, (c) => map[c] ?? c)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'auglysing'
  );
}
